import { generatePath, Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CrmRecordDetail } from "@/lib/api/crm/masters";
import type { CrmRules } from "@/lib/api/crm/rules";
import { cn } from "@/lib/utils";
import { RoutePath } from "../../routerPaths";
import { descendantsOf } from "../recordHierarchy";
import { AuthUserPanel } from "./AuthUserPanel";
import { recordTypeAccentClasses, recordTypeIcons, recordTypeOrder, recordTypePluralLabels } from "../recordTypes";
import { MoveRecordMasterButton } from "./MoveRecordMasterButton";
import { RecordCard } from "./RecordCard";

/** The mobile login being edited and the one record card it opens under. */
export interface AuthUserPanelTarget {
  recordId: string;
  authUserId: string;
  onClose: () => void;
}

/** A record's card with the records that belong to it nested underneath, grouped by type. */
export function RecordBranch({
  record,
  childrenByOwner,
  rules,
  selectedRecordId,
  authUserPanel,
}: {
  record: CrmRecordDetail;
  childrenByOwner: Map<string, CrmRecordDetail[]>;
  rules: CrmRules;
  selectedRecordId: string | null;
  authUserPanel: AuthUserPanelTarget | null;
}) {
  const children = childrenByOwner.get(record.id) ?? [];
  // Types this record may own per GET /crm/rules, plus any type it actually owns.
  const childTypes = recordTypeOrder.filter((type) =>
    rules.record_types[type].owner_types?.includes(record.record_type)
    || children.some((child) => child.record_type === type));

  return (
    <div className="space-y-4">
      <RecordCard
        record={record}
        fields={rules.record_types[record.record_type].fields}
        selected={record.id === selectedRecordId}
        actions={(
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to={generatePath(RoutePath.CrmEditRecord, { recordId: record.id })} data-testid={`crm-edit-record-${record.id}`}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
            {record.owner_id === null && (
              <MoveRecordMasterButton record={record} descendants={descendantsOf(record.id, childrenByOwner)} />
            )}
          </>
        )}
      />
      {authUserPanel?.recordId === record.id && (
        <AuthUserPanel authUserId={authUserPanel.authUserId} onClose={authUserPanel.onClose} />
      )}
      {childTypes.length > 0 && (
        <div className="ml-5 space-y-6 border-l-2 border-gray-200 pl-5 sm:ml-8 sm:pl-8">
          {childTypes.map((type) => {
            const Icon = recordTypeIcons[type];
            const ofType = children.filter((child) => child.record_type === type);
            return (
              <div key={type} className="space-y-3" data-testid={`crm-children-${record.id}-${type}`}>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <span className={cn("flex h-6 w-6 items-center justify-center rounded-md", recordTypeAccentClasses[type])}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  {recordTypePluralLabels[type]}
                  <span className="rounded-full bg-gray-200 px-1.5 py-px text-[11px] font-semibold text-gray-600">
                    {ofType.length}
                  </span>
                </h4>
                {ofType.length === 0 ? (
                  <p className="text-sm text-gray-400">No {recordTypePluralLabels[type].toLowerCase()} yet</p>
                ) : (
                  ofType.map((child) => (
                    <RecordBranch
                      key={child.id}
                      record={child}
                      childrenByOwner={childrenByOwner}
                      rules={rules}
                      selectedRecordId={selectedRecordId}
                      authUserPanel={authUserPanel}
                    />
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
